import React, {useState} from "react";
import IconLabel from "./IconLabel";
import {CheckIcon, XIcon} from "./icons";
import {Button, Form, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";

/**
 * Modal with a small form for adding/editing a row of the page's form.
 *
 * The form body is mounted only while the modal is open, so its state starts
 * fresh from `initial` every time. `children` is a render function receiving
 * the current values and a `set(patch)` updater.
 *
 * @template T
 * @param {{
 *   isOpen: boolean,
 *   toggle: () => void,
 *   title: string,
 *   initial: T,
 *   canSave?: (values: T) => boolean,
 *   onSave: (values: T) => void,
 *   children: (values: T, set: (patch: Partial<T>) => void) => React.ReactNode,
 * }} props
 */
const FormModal = ({isOpen, toggle, title, initial, canSave, onSave, children}) => (
    <Modal isOpen={isOpen} toggle={toggle} fade={false} centered>
        <FormBody title={title} initial={initial} canSave={canSave} onSave={onSave} onCancel={toggle}>
            {children}
        </FormBody>
    </Modal>
);

const FormBody = ({title, initial, canSave, onSave, onCancel, children}) => {
    const [values, setValues] = useState(initial);
    const set = (patch) => setValues((v) => ({...v, ...patch}));

    const handleSubmit = (event) => {
        // Rendered in a portal: React would bubble this submit up to the page
        // form the modal is declared inside of. Keep it local.
        event.preventDefault();
        event.stopPropagation();
        if (!canSave || canSave(values)) onSave(values);
    };

    return (
        <Form onSubmit={handleSubmit} noValidate>
            <ModalHeader toggle={onCancel}>{title}</ModalHeader>
            <ModalBody>{children(values, set)}</ModalBody>
            <ModalFooter>
                <Button color="secondary" outline type="button" onClick={onCancel}>
                    <IconLabel icon={<XIcon />}>Cancelar</IconLabel>
                </Button>
                <Button color="primary" type="submit" disabled={canSave ? !canSave(values) : false}>
                    <IconLabel icon={<CheckIcon />}>Salvar</IconLabel>
                </Button>
            </ModalFooter>
        </Form>
    );
};

export default FormModal;
